import { Box, Container, Typography, styled } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { analysisPdf } from "Infrastructure/ApiZfp/AnalyzerApi";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

const StyledBackground = styled(Box)`
  background: ${colors.backgroundSecondary};
  height: 100vh;
  width: 100%;
  box-sizing: border-box;
`;

export const AnalysisDocumentPage: FC = () => {
  const { t } = useResource();
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const hasGeneratedRef = useRef(false);

  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  useEffect(() => {
    if (hasGeneratedRef.current) {
      return;
    }

    const generatePdf = async () => {
      if (!analysisId || !versionId) {
        toast.error("Missing analysis or version ID");
        setIsPdfLoading(false);
        return;
      }

      hasGeneratedRef.current = true;

      try {
        const blob = await analysisPdf(analysisId, versionId, undefined, { headers: { Accept: "application/pdf" } });
        const objectUrl = URL.createObjectURL(blob);
        setPdfPreviewUrl(objectUrl);
      } catch (err) {
        console.error(err);
        toast.error(t(Resources.AnalysisDetail.Pdf.LoadError));
      } finally {
        setIsPdfLoading(false);
      }
    };

    generatePdf();
  }, [analysisId, versionId, t]);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  return (
    <StyledBackground>
      <Container maxWidth={false} sx={{ height: "100vh", p: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="center" height="100vh" width="100%">
          {isPdfLoading && <Typography variant="body1">{t(Resources.AnalysisDetail.Pdf.Loading)}</Typography>}
          {!isPdfLoading && pdfPreviewUrl && (
            <iframe
              src={pdfPreviewUrl}
              style={{
                border: 0,
                width: "100%",
                height: "100%",
              }}
              title="PDF Preview"
            />
          )}
        </Box>
      </Container>
    </StyledBackground>
  );
};
